import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import useAxiosConfig from "../config/AxiosConfig";

const ResetUserPasswordModal = ({ open, user, onClose, onSuccess }) => {
  const axiosInstance = useAxiosConfig();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const userId = user?._id || "";
  const fullName = user?.fullName || "";
  const phoneNo = user?.phone_no || "";

  useEffect(() => {
    if (!open) {
      setPassword("");
      setConfirmPassword("");
      setIsSubmitting(false);
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [open]);

  const handleResetPassword = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!userId) {
      setErrorMessage("User id not found.");
      return;
    }

    const isAnyPasswordFieldFilled = !!password || !!confirmPassword;

    // Phone number update scenario is intentionally disabled for now.
    if (!isAnyPasswordFieldFilled) {
      setErrorMessage(
        "Please enter password and confirm password to continue."
      );
      return;
    }

    if (isAnyPasswordFieldFilled) {
      if (!password || !confirmPassword) {
        setErrorMessage("Please fill in both password fields.");
        return;
      }

      if (password !== confirmPassword) {
        setErrorMessage("Password and confirm password do not match.");
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const payload = {
        fullName,
        // Keep phone number unchanged while phone edit flow is disabled.
        phone_no: phoneNo,
      };

      if (isAnyPasswordFieldFilled) {
        payload.password = password;
        payload.confirmPassword = confirmPassword;
      }

      await axiosInstance.put(`/api/admin/edituser/${userId}`, payload);
      if (onSuccess) {
        onSuccess({
          userId,
          phone_no: phoneNo,
        });
      }
      setSuccessMessage("User updated successfully.");
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update user.";
      setErrorMessage(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User Password</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {user ? `User: ${user.fullName || "-"}` : "User password reset modal"}
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          label="Password"
          name="newPassword"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <TextField
          fullWidth
          margin="normal"
          label="Confirm Password"
          name="confirmNewPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
        />
        {!!errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage}
          </Alert>
        )}
        {!!successMessage && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleResetPassword}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#0C7240",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#0C7240",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#B0B0B0",
              color: "white",
            },
          }}
        >
          {isSubmitting ? "Updating..." : "Update User"}
        </Button>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#0C7240",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#0C7240",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetUserPasswordModal;
